import { useEffect, useState } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { BlogPost } from '@/lib/types/contentful'
import { getBlogPostBySlug } from '@/lib/contentful/api'
import { RichTextRenderer } from '@/components/blog/RichTextRenderer'
import { SEO } from '@/components/SEO'
import { RouteDebugger } from '@/components/debug/RouteDebugger'

export default function BlogPostPage() {
  // All hooks must be called at the top level
  const { category, subcategory, slug } = useParams()
  const location = useLocation()
  const [post, setPost] = useState<BlogPost | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Enable debug mode in development or when URL has debug parameter
  const isDebugMode = location.search.includes('debug=true') || import.meta.env.DEV

  // Construct the full slug from URL parameters based on available segments
  const fullSlug = subcategory
    ? `${category}/${subcategory}/${slug}` // Three-segment pattern
    : `${category}/${slug}`               // Two-segment pattern

  useEffect(() => {
    async function fetchPost() {
      try {
        setLoading(true)
        setError(null)
        console.log(`Attempting to fetch blog post with fullSlug: ${fullSlug}`)
        const fetchedPost = await getBlogPostBySlug(fullSlug)

        if (!fetchedPost) {
          console.error(`Blog post not found with slug: ${fullSlug}`)
          setError('Blog post not found. Please check the URL and try again.')
        } else {
          setPost(fetchedPost)
        }
      } catch (err) {
        console.error('Error fetching blog post:', err)
        setError(err instanceof Error ? err.message : 'Failed to load blog post')
      } finally {
        setLoading(false)
      }
    }

    fetchPost()
  }, [fullSlug])

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto space-y-4 animate-pulse bg-card rounded-lg overflow-hidden border border-border/10 p-8 shadow-sm">
          <div className="h-8 bg-muted rounded w-3/4"></div>
          <div className="h-4 bg-muted rounded w-1/4"></div>
          <div className="aspect-video bg-muted rounded-lg"></div>
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded"></div>
            <div className="h-4 bg-muted rounded"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </div>
      </div>
    )
  }

  // We already have location and isDebugMode defined at the top of the component

  if (error || !post) {
    return (
      <div className="container mx-auto px-4 py-12">
        <RouteDebugger enabled={isDebugMode} />
        <div className="max-w-3xl mx-auto text-center bg-card rounded-lg overflow-hidden border border-border/10 p-8 shadow-sm">
          <h1 className="text-2xl font-bold mb-4 text-foreground">Error Loading Post</h1>
          <p className="text-muted-foreground mb-4">
            {error || 'Post not found. Please check the URL and try again.'}
          </p>
          {isDebugMode && (
            <div className="mt-6 p-4 bg-gray-800 text-white rounded text-left text-sm overflow-auto max-h-60">
              <p className="font-bold mb-2">Debug Information:</p>
              <p>URL Path: {location.pathname}</p>
              <p>Full Slug: {fullSlug}</p>
              <p>Category: {category}</p>
              <p>Subcategory: {subcategory || 'N/A'}</p>
              <p>Slug: {slug}</p>
            </div>
          )}
          <div className="mt-6">
            <a href="/blog" className="text-primary hover:underline">Return to Blog</a>
          </div>
        </div>
      </div>
    )
  }

  const {
    title,
    metaDescription,
    content,
    featuredImage,
    imageAltText,
    readTime,
  } = post.fields

  return (
    <>
      <RouteDebugger enabled={isDebugMode} />
      <SEO
        title={`${title} - WithWords Blog`}
        description={metaDescription}
      />
      <article className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto bg-card rounded-lg overflow-hidden border border-border/10 p-8 shadow-sm">
          <header className="mb-8">
            <h1 className="text-4xl font-bold mb-4 text-foreground">{title}</h1>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {readTime && <span>{readTime}</span>}
            </div>
          </header>

          {featuredImage?.fields?.file?.url && (
            <div className="mb-8">
              <img
                src={`https:${featuredImage.fields.file.url}`}
                alt={imageAltText || title}
                className="w-full rounded-lg shadow-lg"
              />
            </div>
          )}

          <div className="prose prose-lg prose-gray dark:prose-invert max-w-none text-foreground
            prose-headings:text-foreground
            prose-p:text-foreground
            prose-strong:text-foreground
            prose-a:text-primary hover:prose-a:text-primary/80
            prose-ul:text-foreground
            prose-ol:text-foreground">
            <RichTextRenderer content={content} />
          </div>
        </div>
      </article>
    </>
  )
}
